import React from "react";
import { Grid } from "@mui/material";
import { formatDate } from "@telerik/kendo-intl";
import { VarianceRequest } from "../../../../../../services/varianceService/model/varianceRequest/VarianceRequest";
import { useCenterListQuery } from "../../../../../../services/orgService/org-api";
import { SeverityItems } from "../../severity/SeverityItems";

// Brand colors from Legent Health / PSN brand guidelines
const brand = {
    navy: "#041367",
    oceanBlue: "#3E87F5",
    lightBlue: "#DCF3F9",
    psnTeal: "#62BCBD",
};

// Reusable inline styles for PDF rendering (Kendo PDFExport requires inline styles)
const styles = {
    page: { fontFamily: "'Proxima Nova', Arial, Helvetica, sans-serif", fontSize: "9pt", color: brand.navy } as React.CSSProperties,
    title: { textAlign: "center", fontWeight: "bold", fontSize: "16pt", marginBottom: 4, color: brand.navy } as React.CSSProperties,
    subtitle: { textAlign: "center", fontSize: "10pt", color: brand.oceanBlue, marginBottom: 16 } as React.CSSProperties,
    sectionHeader: { fontSize: "11pt", fontWeight: "bold", borderBottom: `2px solid ${brand.psnTeal}`, paddingBottom: 2, marginTop: 16, marginBottom: 8, color: brand.navy } as React.CSSProperties,
    label: { fontSize: "8pt", fontWeight: "bold", color: "#555" } as React.CSSProperties,
    value: { fontSize: "9pt", minHeight: "14pt", paddingBottom: 2 } as React.CSSProperties,
    chipRow: { fontSize: "9pt", display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 4 } as React.CSSProperties,
    chip: { display: "inline-block", padding: "2px 8px", borderRadius: 12, backgroundColor: brand.lightBlue, fontSize: "8pt", border: `1px solid ${brand.psnTeal}` } as React.CSSProperties,
    tableHeader: { fontSize: "8pt", fontWeight: "bold", borderBottom: `1px solid ${brand.navy}`, padding: "4px 6px", backgroundColor: brand.lightBlue } as React.CSSProperties,
    tableCell: { fontSize: "8pt", borderBottom: "1px solid #ddd", padding: "4px 6px" } as React.CSSProperties,
    footer: { marginTop: 24, paddingTop: 8, borderTop: `1px solid ${brand.psnTeal}`, fontSize: "7pt", color: "#777", textAlign: "center" } as React.CSSProperties,
    boolLabel: { fontSize: "9pt", marginBottom: 2 } as React.CSSProperties,
};

function fmtDate(d: Date | string | undefined | null, format = "MM/dd/yyyy h:mm a"): string {
    if (!d) return "";
    const date = typeof d === "string" ? new Date(d) : d;
    if (isNaN(date.getTime())) return "";
    return formatDate(date, format);
}

function fieldVal(val: string | number | undefined | null, fallback = "—"): string {
    if (val === undefined || val === null || val === "") return fallback;
    return String(val);
}

function boolField(label: string, val: boolean | undefined, detail?: string): React.ReactNode {
    if (!val) return null;
    return (
        <div style={styles.boolLabel}>
            ✓ {label}
            {detail ? `: ${detail}` : ""}
        </div>
    );
}

interface PrintableIncidentReportProps {
    variance: VarianceRequest;
}

function PrintableIncidentReport({ variance }: PrintableIncidentReportProps) {
    const { data: centers } = useCenterListQuery();
    const centerName = centers?.find(c => c.code === variance.center)?.name ?? variance.center;
    const checkedTypes = variance.incidentTypeDetails?.filter(t => t.checked) ?? [];
    const checkedSubTypes = variance.incidentSubTypeDetails?.filter(t => t.checked) ?? [];
    const hasFollowUp = variance.followUp !== undefined && variance.followUp !== null;
    const hasDirectorAction = variance.directorResponseAction !== undefined && variance.directorResponseAction !== null;
    const hasFallAction = hasDirectorAction && variance.directorResponseAction?.fallAction !== undefined;

    return (
        <div style={styles.page}>
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: 8 }}>
                <img src="/psn-logo.png" alt="PSN Physicians Surgical Network Affiliates" style={{ height: 40, marginBottom: 6 }} />
            </div>
            <div style={styles.title}>Incident Report</div>
            <div style={styles.subtitle}>
                {variance.incidentDisplayNumber || "Draft"}
            </div>

            {/* Incident Overview */}
            <div style={styles.sectionHeader}>Incident Overview</div>
            <Grid container spacing={1}>
                <Grid item xs={3}>
                    <div style={styles.label}>Incident Number</div>
                    <div style={styles.value}>{fieldVal(variance.incidentDisplayNumber)}</div>
                </Grid>
                <Grid item xs={3}>
                    <div style={styles.label}>Status</div>
                    <div style={styles.value}>{fieldVal(variance.incidentDisplayStatus)}</div>
                </Grid>
                <Grid item xs={3}>
                    <div style={styles.label}>Incident Date</div>
                    <div style={styles.value}>{fmtDate(variance.incidentDate, "MM/dd/yyyy")}</div>
                </Grid>
                <Grid item xs={3}>
                    <div style={styles.label}>Report Date</div>
                    <div style={styles.value}>{fmtDate(variance.varianceStartDate, "MM/dd/yyyy")}</div>
                </Grid>
                {variance.completeDate && variance.status === "Complete" && (
                    <Grid item xs={3}>
                        <div style={styles.label}>Closed/Completed Date</div>
                        <div style={styles.value}>{fmtDate(variance.completeDate, "MM/dd/yyyy h:mm a")}</div>
                    </Grid>
                )}
            </Grid>
            <Grid container spacing={1} style={{ marginTop: 4 }}>
                <Grid item xs={3}>
                    <div style={styles.label}>Center</div>
                    <div style={styles.value}>{fieldVal(centerName)}</div>
                </Grid>
                <Grid item xs={3}>
                    <div style={styles.label}>Reporting Department</div>
                    <div style={styles.value}>{fieldVal(variance.reportingDepartment)}</div>
                </Grid>
                <Grid item xs={4}>
                    <div style={styles.label}>Department</div>
                    <div style={styles.value}>{fieldVal(variance.department)}</div>
                </Grid>
            </Grid>

            {/* Patient / Individual */}
            <div style={styles.sectionHeader}>Patient / Individual</div>
            <Grid container spacing={1}>
                <Grid item xs={3}>
                    <div style={styles.label}>MRN</div>
                    <div style={styles.value}>{fieldVal(variance.individual?.mrn)}</div>
                </Grid>
                <Grid item xs={3}>
                    <div style={styles.label}>Name</div>
                    <div style={styles.value}>
                        {variance.individual?.firstName || variance.individual?.lastName
                            ? `${fieldVal(variance.individual?.lastName)}, ${fieldVal(variance.individual?.firstName)}`
                            : "—"}
                    </div>
                </Grid>
                <Grid item xs={2}>
                    <div style={styles.label}>Age</div>
                    <div style={styles.value}>{variance.individual?.age ? String(variance.individual.age) : "—"}</div>
                </Grid>
                <Grid item xs={2}>
                    <div style={styles.label}>Sex</div>
                    <div style={styles.value}>{fieldVal(variance.individual?.sex)}</div>
                </Grid>
                <Grid item xs={2}>
                    <div style={styles.label}>Type</div>
                    <div style={styles.value}>{fieldVal(variance.individual?.individualType)}</div>
                </Grid>
            </Grid>

            {/* Incident Type & Subtypes */}
            <div style={styles.sectionHeader}>Incident Classification</div>
            <Grid container spacing={1}>
                <Grid item xs={4}>
                    <div style={styles.label}>Incident Type</div>
                    <div style={styles.value}>{fieldVal(variance.incidentType)}</div>
                </Grid>
                <Grid item xs={8}>
                    <div style={styles.label}>Severity / Harm Level</div>
                    <div style={styles.value}>
                        {SeverityItems.find(s => s.value === variance.severity)?.description
                            ?? fieldVal(variance.severity, "Not Assessed")}
                    </div>
                </Grid>
            </Grid>
            {checkedTypes.length > 0 && (
                <div style={{ marginTop: 6 }}>
                    <div style={styles.label}>Type Details</div>
                    <div style={styles.chipRow}>
                        {checkedTypes.map((t, i) => (
                            <span key={i} style={styles.chip}>{t.description || t.name}</span>
                        ))}
                    </div>
                </div>
            )}
            {checkedSubTypes.length > 0 && (
                <div style={{ marginTop: 4 }}>
                    <div style={styles.label}>Subtype Details</div>
                    <div style={styles.chipRow}>
                        {checkedSubTypes.map((t, i) => (
                            <span key={i} style={styles.chip}>{t.description || t.name}</span>
                        ))}
                    </div>
                </div>
            )}

            {/* Medication Details (conditional) */}
            {variance.medicationDetail && (variance.medicationDetail.name || variance.medicationDetail.route || variance.medicationDetail.dose > 0) && (
                <>
                    <div style={styles.sectionHeader}>Medication Details</div>
                    <Grid container spacing={1}>
                        <Grid item xs={4}>
                            <div style={styles.label}>Medication Name</div>
                            <div style={styles.value}>{fieldVal(variance.medicationDetail.name)}</div>
                        </Grid>
                        <Grid item xs={4}>
                            <div style={styles.label}>Route</div>
                            <div style={styles.value}>{fieldVal(variance.medicationDetail.route)}</div>
                        </Grid>
                        <Grid item xs={4}>
                            <div style={styles.label}>Dose</div>
                            <div style={styles.value}>{variance.medicationDetail.dose > 0 ? String(variance.medicationDetail.dose) : "—"}</div>
                        </Grid>
                    </Grid>
                </>
            )}

            {/* Description */}
            <div style={styles.sectionHeader}>Description</div>
            <div style={{ ...styles.value, whiteSpace: "pre-wrap", lineHeight: 1.4 }}>
                {fieldVal(variance.description, "No description provided.")}
            </div>

            {/* Follow-Up */}
            {hasFollowUp && (
                <>
                    <div style={styles.sectionHeader}>Follow-Up Actions</div>
                    {boolField("Physician Notified", variance.followUp!.physicianNotified, variance.followUp!.physicanNotifiedName)}
                    {boolField("Physician Examined", variance.followUp!.physicianExamined, variance.followUp!.physicianExaminedName)}
                    {boolField("Lab Ordered", variance.followUp!.labOrdered, variance.followUp!.labList)}
                    {boolField("Family Notified", variance.followUp!.familyNotified, variance.followUp!.familyNotifiedName)}
                    {boolField("Charge Nurse Notified", variance.followUp!.chargeNurseNotified, variance.followUp!.chargeNurseName)}
                    {variance.followUp!.otherActions && (
                        <div style={{ marginTop: 4 }}>
                            <div style={styles.label}>Other Actions</div>
                            <div style={{ ...styles.value, whiteSpace: "pre-wrap" }}>{variance.followUp!.otherActions}</div>
                        </div>
                    )}
                </>
            )}

            {/* Director Response */}
            {(variance.directorResponse || hasDirectorAction || (variance.directorResponses && variance.directorResponses.length > 0)) && (
                <>
                    <div style={styles.sectionHeader}>Director Response</div>

                    {/* Legacy single response (backward compatibility) */}
                    {variance.directorResponse && (
                        <div style={{ marginBottom: 8 }}>
                            {variance.directorResponseBy && variance.directorResponseDate && (
                                <div style={{ fontSize: "8pt", color: "#666" }}>
                                    {variance.directorResponseBy} — {fmtDate(variance.directorResponseDate, "MM/dd/yyyy h:mm a")}
                                </div>
                            )}
                            <div style={{ ...styles.value, whiteSpace: "pre-wrap" }}>
                                {variance.directorResponse}
                            </div>
                        </div>
                    )}

                    {/* Multiple director responses */}
                    {variance.directorResponses && variance.directorResponses.length > 0 && (
                        variance.directorResponses
                            .slice()
                            .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
                            .map((r, i) => (
                                <div key={r.directorResponseId || i} style={{ marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #eee" }}>
                                    <div style={{ fontSize: "8pt", color: "#666" }}>
                                        {r.createdBy} — {fmtDate(r.createdDate)}
                                        {r.editedBy && r.editedDate && (
                                            <span> (edited by {r.editedBy} on {fmtDate(r.editedDate)})</span>
                                        )}
                                    </div>
                                    <div style={{ ...styles.value, whiteSpace: "pre-wrap" }}>{r.text}</div>
                                </div>
                            ))
                    )}

                    {hasDirectorAction && (
                        <div>
                            {boolField("Follow-Up Required", variance.directorResponseAction!.isFollowup)}
                            {boolField("Root Cause Identified", variance.directorResponseAction!.hasRootCause)}
                            {boolField("Reportable", variance.directorResponseAction!.isReportable, variance.directorResponseAction!.reportAgency)}
                            {boolField("Board Recommendation", variance.directorResponseAction!.hasBoardRecommendation, variance.directorResponseAction!.boardRecommendation)}
                        </div>
                    )}
                    {hasFallAction && (
                        <div style={{ marginTop: 8 }}>
                            <div style={styles.label}>Fall Investigation</div>
                            {boolField("Foreign Substance", variance.directorResponseAction!.fallAction!.foreignSubstance, variance.directorResponseAction!.fallAction!.foreignSubstanceDetails)}
                            {boolField("Hazardous Environment", variance.directorResponseAction!.fallAction!.hazardEnviroment, variance.directorResponseAction!.fallAction!.hazardEnviromentDetails)}
                            {boolField("Patient Fall Risk", variance.directorResponseAction!.fallAction!.patientFallRisk, variance.directorResponseAction!.fallAction!.patientFallRiskDetails)}
                            {boolField("Intervention Documented", variance.directorResponseAction!.fallAction!.interventionDocumented)}
                            {boolField("Toileting Needs Addressed", variance.directorResponseAction!.fallAction!.toiletingNeeds)}
                            {boolField("Meds/Imaging Ordered", variance.directorResponseAction!.fallAction!.medsImagingOrdered, variance.directorResponseAction!.fallAction!.medsImagingOrderedDetails)}
                            {boolField("Injuries Identified", variance.directorResponseAction!.fallAction!.injuriesIdentified, variance.directorResponseAction!.fallAction!.injuriesIdentifiedDetails)}
                        </div>
                    )}
                </>
            )}

            {/* Witnesses */}
            {variance.witnesses && variance.witnesses.length > 0 && (
                <>
                    <div style={styles.sectionHeader}>Witnesses</div>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <td style={styles.tableHeader}>Name</td>
                                <td style={styles.tableHeader}>Phone</td>
                                <td style={styles.tableHeader}>Email</td>
                            </tr>
                        </thead>
                        <tbody>
                            {variance.witnesses.map((w, i) => (
                                <tr key={i}>
                                    <td style={styles.tableCell}>{`${w.firstName} ${w.lastName}`.trim() || "—"}</td>
                                    <td style={styles.tableCell}>{w.homePhone || w.mobilePhone || "—"}</td>
                                    <td style={styles.tableCell}>{w.email || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {/* Comments */}
            {variance.comments && variance.comments.length > 0 && (
                <>
                    <div style={styles.sectionHeader}>Comments</div>
                    {variance.comments.map((c, i) => (
                        <div key={i} style={{ marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #eee" }}>
                            <div style={{ fontSize: "8pt", color: "#666" }}>
                                {c.addedBy} — {fmtDate(c.addDate)}
                            </div>
                            <div style={{ ...styles.value, whiteSpace: "pre-wrap" }}>{c.text}</div>
                        </div>
                    ))}
                </>
            )}

            {/* Documents */}
            {variance.documents && variance.documents.filter(d => !d.isDeleted).length > 0 && (
                <>
                    <div style={styles.sectionHeader}>Documents</div>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <td style={styles.tableHeader}>File Name</td>
                                <td style={styles.tableHeader}>Uploaded By</td>
                                <td style={styles.tableHeader}>Upload Date</td>
                            </tr>
                        </thead>
                        <tbody>
                            {variance.documents.filter(d => !d.isDeleted).map((d, i) => (
                                <tr key={i}>
                                    <td style={styles.tableCell}>{d.fileName}{d.extension ? `.${d.extension}` : ""}</td>
                                    <td style={styles.tableCell}>{d.uploadedBy || "—"}</td>
                                    <td style={styles.tableCell}>{fmtDate(d.uploadDate, "MM/dd/yyyy")}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {/* Assigned Users */}
            {variance.assignedUsers && variance.assignedUsers.length > 0 && (
                <>
                    <div style={styles.sectionHeader}>Assigned Users</div>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <td style={styles.tableHeader}>Name</td>
                                <td style={styles.tableHeader}>Email</td>
                                <td style={styles.tableHeader}>Status</td>
                            </tr>
                        </thead>
                        <tbody>
                            {variance.assignedUsers.map((u, i) => (
                                <tr key={i}>
                                    <td style={styles.tableCell}>{u.displayName || "—"}</td>
                                    <td style={styles.tableCell}>{u.email || "—"}</td>
                                    <td style={styles.tableCell}>{u.isClosed ? "Closed" : "Open"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {/* Footer */}
            <div style={styles.footer}>
                CONFIDENTIAL — This incident report is prepared for quality improvement purposes and may be protected under applicable peer review and quality assurance statutes.
                <br />
                Generated {fmtDate(new Date())} • {variance.incidentDisplayNumber}
            </div>
        </div>
    );
}

export { PrintableIncidentReport };
